import { useLocation, useNavigate } from 'react-router-dom';
import { useAccount, useAuth } from '../../auth';
import { BreadcrumbItem, BreadcrumbMenu } from '../header/BreadcrumbNav';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { capitalize } from '../../utils/strings';
import { formatDistanceToNow, parseJSON } from 'date-fns';
import type { ActiveAssumedRole } from '../../types/global.types';

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  creator: 'Creator',
  'account-admin': 'Admin',
};

const INTERNAL_ADMIN_KEY = '__internal_admin__';
const ASSUMED_KEY_PREFIX = '__assumed__';

export const AccountsMenu = (props: any) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentAccount, switchAccount } = useAccount();
  let lastSelectedAccount: any = null;
  try {
    const stored = localStorage.getItem('currentAccount');
    lastSelectedAccount = stored ? JSON.parse(stored) : null;
  } catch (e) {
    lastSelectedAccount = null;
  }

  const isInternal = Boolean(auth.user?.internal_role);
  const assumedRoles: ActiveAssumedRole[] =
    auth.user?.active_assumed_roles || [];
  const isOnInternalPage = location.pathname.startsWith('/internal');

  const [selectCompany, setSelectCompany] = React.useState<any>(() => {
    if (isOnInternalPage) {
      return { key: INTERNAL_ADMIN_KEY };
    }
    if (currentAccount) {
      return { ...currentAccount, key: currentAccount.title };
    }
    if (lastSelectedAccount) {
      return { ...lastSelectedAccount, key: lastSelectedAccount.title };
    }
    return null;
  });

  const selectedCompany =
    typeof selectCompany == 'string'
      ? JSON.parse(selectCompany)
      : selectCompany;
  const pathComponents = location.pathname.split('/');

  // Regular account items
  const accountItems: any[] = [];
  for (const role of auth.user.roles) {
    let path = `/${role.account.name}`;
    if (pathComponents.length > 2 && !isOnInternalPage) {
      path = path + '/' + pathComponents[2];
    } else {
      path = path + '/flows';
    }

    accountItems.push({
      title: role.account.title,
      name: role.account.name,
      key: role.account.title,
      path: path,
      account: role.account,
    });
  }

  // Assumed account items
  const assumedItems: any[] = assumedRoles.map((ar) => {
    const key = `${ASSUMED_KEY_PREFIX}${ar.account_id}`;
    return {
      title: ar.account_title || ar.account_name,
      name: ar.account_name,
      key,
      path: `/${ar.account_name}/flows`,
      account: {
        id: ar.account_id,
        name: ar.account_name,
        title: ar.account_title,
      },
      assumedRole: ar,
    };
  });

  // Determine selected value
  const selectedKey = isOnInternalPage
    ? INTERNAL_ADMIN_KEY
    : selectedCompany?.key || '';

  return (
    <FormControl fullWidth variant="standard">
      <Select
        disabled={accountItems.length === 0 && !isInternal}
        label="Company"
        id="company-select"
        value={selectedKey}
        MenuProps={{
          marginThreshold: 0,
        }}
        sx={(theme) => ({
          '& .MuiSelect-select': {
            padding: `${theme.spacing(1)} 0 ${theme.spacing(1)} ${theme.spacing(4)}`,
            ...(props?.layoutState === 'closed' && { paddingLeft: '5px' }),
            color: '#1565C0',
            textAlign: 'center',
            borderTop: '1px solid #fff',
            background: theme.palette.common.white,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
          },

          '& .Mui-disabled.MuiSelect-select': {
            padding: `${theme.spacing(3)} 0 ${theme.spacing(3)} ${theme.spacing(4)}`,
            ...(props?.layoutState === 'closed' && { paddingLeft: '5px' }),
            color: '#1565C0',
            fontFamily: 'Source Serif Pro',
            fontWeight: 700,
            textAlign: 'center',
            '-webkit-text-fill-color': '#1565C0',
            '& .MuiSvgIcon-root': { display: 'none' },
          },
          '& .Mui-disabled.MuiSelect-icon': {
            display: 'none',
          },
        })}
        renderValue={(selected) => {
          if (
            !selected ||
            (typeof selected === 'string' && selected.length === 0)
          ) {
            return <em>Select Company</em>;
          }

          if (selected === INTERNAL_ADMIN_KEY) {
            return props?.layoutState === 'closed' ? 'IA' : 'Internal Admin';
          }

          if (
            typeof selected === 'string' &&
            selected.startsWith(ASSUMED_KEY_PREFIX)
          ) {
            const assumed = assumedItems.find((i) => i.key === selected);
            if (assumed) {
              return props?.layoutState === 'closed'
                ? assumed.title.split(' ').map((s: string) => capitalize(s[0]))
                : assumed.title;
            }
          }

          return props?.layoutState === 'closed'
            ? selected.split(' ').map((s: string) => capitalize(s[0]))
            : selected;
        }}
        onChange={(event) => {
          const value = event.target.value;

          // Internal Admin entry
          if (value === INTERNAL_ADMIN_KEY) {
            setSelectCompany({ key: INTERNAL_ADMIN_KEY });
            navigate('/internal');
            return;
          }

          // Assumed account entry
          const assumed = assumedItems.find((item) => item.key === value);
          if (assumed) {
            switchAccount(assumed.account);
            localStorage.setItem('currentAccount', JSON.stringify(assumed));
            setSelectCompany(assumed);
            navigate(assumed.path);
            return;
          }

          // Regular account entry
          const role = accountItems.find((item) => item.key === value);
          if (role) {
            switchAccount(role.account);
            localStorage.setItem('currentAccount', JSON.stringify(role));
            setSelectCompany(role);
            navigate(role.path);
          }
        }}
      >
        {/* Regular accounts */}
        {accountItems.map((item) => (
          <MenuItem key={item.key} value={item.key}>
            {item.title}
          </MenuItem>
        ))}

        {/* Assumed accounts section */}
        {assumedItems.length > 0 && <Divider />}
        {assumedItems.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontSize: '0.75rem' }}>
            Assumed Roles
          </ListSubheader>
        )}
        {assumedItems.map((item) => (
          <MenuItem key={item.key} value={item.key}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{item.title}</span>
                <Chip
                  label={
                    ROLE_LABELS[item.assumedRole.assumed_role] ||
                    item.assumedRole.assumed_role
                  }
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                expires{' '}
                {formatDistanceToNow(parseJSON(item.assumedRole.expires_at), {
                  addSuffix: true,
                })}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        {/* Internal Admin entry */}
        {isInternal && <Divider />}
        {isInternal && (
          <MenuItem value={INTERNAL_ADMIN_KEY}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>Internal Admin</span>
              <Chip
                label="Internal"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Box>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};
